/**
 * The right drawer's "Your conversations": the person's Lykke threads. The panel holds the one conversation that
 * follows them everywhere; a campaign's own earlier conversation (classic layout, cowork per campaign threads) opens
 * here as a second chat beside the page.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, MessageSquareText } from 'lucide-react';
import { lykkeApi } from '@/cowork/lykke/lykkeApi';
import { CampaignLykkePanel } from '@/cowork/lykke/LykkeDock';
import { useShell } from './ShellContext';

const when = (iso: string) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return ''; } };

export function ConversationsDrawer() {
  const shell = useShell();
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['lykke', 'threads'], queryFn: () => lykkeApi.threads(), staleTime: 30_000, retry: 1 });
  const threads = q.data?.threads || [];
  if (openId) {
    const t = threads.find((x) => x.campaignId === openId);
    return (
      <div className="flex h-full min-h-0 flex-col">
        <button type="button" onClick={() => setOpenId(null)} className="mx-4 mb-2 inline-flex items-center gap-1.5 self-start text-[12.5px] font-semibold text-[#7a1fbf] hover:underline"><ArrowLeft className="h-3.5 w-3.5" />All conversations</button>
        <div className="px-4 pb-1 text-[13px] font-semibold text-[var(--shell-text)]">{t?.title || 'Campaign'}</div>
        <CampaignLykkePanel campaignId={openId} className="min-h-0 flex-1" />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1 px-2">
      <button type="button" onClick={() => { shell?.closeDrawer(); shell?.setView('lykke'); shell?.focusAgent(); }}
        className="flex items-start gap-2.5 rounded-xl px-2.5 py-2 text-left hover:bg-[var(--shell-hover)]">
        <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-[#7a1fbf]" />
        <span className="min-w-0"><span className="block text-[13.5px] font-semibold text-[var(--shell-text)]">Lykke, on every page</span><span className="block text-[12px] text-[var(--shell-muted)]">The conversation in the panel</span></span>
      </button>
      {q.isLoading && <div className="flex items-center gap-2 px-2.5 py-2 text-[12.5px] text-[var(--shell-muted)]"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading</div>}
      {threads.filter((t) => t.campaignId).map((t) => (
        <button key={t.id} type="button" onClick={() => setOpenId(t.campaignId)} className="flex items-start gap-2.5 rounded-xl px-2.5 py-2 text-left hover:bg-[var(--shell-hover)]">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shell-muted)]" />
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline gap-2"><span className="truncate text-[13.5px] font-semibold text-[var(--shell-text)]">{t.title}</span><span className="ml-auto shrink-0 text-[11.5px] text-[var(--shell-muted)]">{when(t.updatedAt)}</span></span>
            {t.lastText && <span className="block truncate text-[12px] text-[var(--shell-muted)]">{t.lastText}</span>}
          </span>
        </button>
      ))}
      {!q.isLoading && !threads.some((t) => t.campaignId) && <p className="px-2.5 py-2 text-[12.5px] text-[var(--shell-muted)]">No campaign conversations yet.</p>}
    </div>
  );
}
